// ======================================================================
// 📁 File: src/modules/service_crm/layout/ServiceCRMSidebar.jsx
// 📝 Purpose:
//   Sidebar navigation for Service CRM module
//   - RBAC-driven
//   - Collapsible sidebar
//   - ERP-standard grouped navigation
// ======================================================================

import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";

// ----------------------------------------------------------------------
// Permission Helper
// ----------------------------------------------------------------------
const canAny = (permissions, keys = []) =>
  keys.some((k) => permissions?.[k]?.view === true);

// ----------------------------------------------------------------------
// Sidebar Menu Definition
// ----------------------------------------------------------------------
const MENU = [

  // ===================== DASHBOARD =====================
  {
    label: "Dashboard",
    route: "/service-crm/dashboard",
    //perms: ["SERVICE_CRM_DASHBOARD"],
    perms: ["SCM_DASHBOARD"],
    //perms: ["PPE_VIEW_DASHBOARD"],

    icon: "fas fa-chart-line",
  },

  // ===================== OPERATIONS =====================
  {
    type: "group",
    label: "Operations",
  },

  {
    label: "Installed Products",
    route: "/service-crm/installed-products",
    //perms: ["SCM_INSTALLED_PRODUCT_VIEW"],
    //perms: ["PPE_VIEW_DASHBOARD"],
    perms: ["SCM_INSTALLED_PRODUCT"],
    icon: "fas fa-industry",
  },

  {
    label: "Service Tickets",
    route: "/service-crm/tickets",
    //perms: ["SCM_TICKET_VIEW"],
    //perms: ["PPE_VIEW_DASHBOARD"],
    perms: ["SERVICE_TICKET"],

    icon: "fas fa-tools",
  },

  {
    label: "Service Visits",
    route: "/service-crm/visits",
    //perms: ["SCM_VISIT_VIEW"],
    //perms: ["PPE_VIEW_DASHBOARD"],
    perms: ["SERVICE_VISIT"],
    icon: "fas fa-user-cog",
  },

  // ===================== CUSTOMER EXPERIENCE =====================
  {
    type: "group",
    label: "Customer Experience",
  },

  {
    label: "Satisfaction",
    route: "/service-crm/satisfaction",
    //perms: ["SCM_SATISFACTION_VIEW"],
    //perms: ["PPE_VIEW_DASHBOARD"],
    perms: ["SERVICE_SATISFACTION"],
    
    icon: "fas fa-smile",
  },

  {
    label: "Feedback",
    route: "/service-crm/feedback",
    //perms: ["SCM_FEEDBACK_VIEW"],
    //perms: ["PPE_VIEW_DASHBOARD"],
    perms: ["SERVICE_FEEDBACK"],
    icon: "fas fa-comments",
  },

  // ===================== REPORTS =====================
  {
    type: "group",
    label: "Reports",
  },

  {
  label: "Installed Product Report",
  route: "/service-crm/reports/installed-products",
  //perms: ["PPE_VIEW_DASHBOARD"],
  perms: ["SCM_REPORT_INSTALLED_PRODUCTS"],
  icon: "fas fa-industry",
},

  {
  label: "Service Ticket Report",
  route: "/service-crm/reports/tickets",
  //perms: ["PPE_VIEW_DASHBOARD"],
  perms: ["SCM_REPORT_TICKETS"],
  icon: "fas fa-file-alt",
},

{
  label: "Feedback Report",
  route: "/service-crm/reports/feedback",
  perms: ["PPE_VIEW_DASHBOARD"],
  perms: ["SCM_REPORT_FEEDBACK"],
  icon: "fas fa-comments",
},

{
  label: "Satisfaction Report",
  route: "/service-crm/reports/satisfaction",
  //perms: ["PPE_VIEW_DASHBOARD"],
perms: ["SCM_REPORT_SATISFACTION"],
  icon: "fas fa-smile",
},


// ===================== MASTERS =====================
{
  type: "group",
  label: "Masters",
},

{
  label: "Business Partner",
  route: "/service-crm/masters/business-partner",
  //perms: ["PPE_VIEW_DASHBOARD"],
  perms: ["SCM_BUSINESS_PARTNER"],
  icon: "fas fa-address-book",
},
{
  label: "Business Partner Assignment",
  route: "/service-crm/masters/business-partner-assignment",
  perms: ["SCM_BP_ASSIGNMENT"],
  icon: "fas fa-link",
},
  

// ===================== CONFIGURATION =====================
  {
    type: "group",
    label: "Configuration",
  },

  {
    label: "SLA Configuration",
    route: "/service-crm/sla",
    //perms: ["SCM_SLA_MANAGE"],
    //perms: ["PPE_VIEW_DASHBOARD"],
    perms: ["SCM_SLA_MANAGE"],
    
    icon: "fas fa-clock",
  },
];

// ======================================================================
// Sidebar Component
// ======================================================================
export default function ServiceCRMSidebar({ onToggle }) {

  const { permissions } = useContext(AuthContext);
  const location = useLocation();

  const collapsedLS = localStorage.getItem(
    "service_crm_sidebar_collapsed"
  );

  const [expanded, setExpanded] = useState(
    collapsedLS === null ? true : collapsedLS === "false"
  );

  useEffect(() => {
    localStorage.setItem(
      "service_crm_sidebar_collapsed",
      expanded ? "false" : "true"
    );

    onToggle?.(expanded);

  }, [expanded, onToggle]);

  return (
    <aside
      style={{
        width: expanded ? 260 : 64,
        transition: "width 0.22s ease",
        background: "#111827",
        color: "#fff",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >

      {/* Header */}
      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          padding: expanded ? "0 12px" : "0",
          borderBottom: "1px solid #374151",
        }}
      >
        {expanded && (
          <span style={{ fontWeight: 700 }}>
            Service CRM
          </span>
        )}

        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
          }}
        >
          <i className="fas fa-bars" />
        </button>
      </div>

      {/* Back to Dashboard */}
      <Link
        to="/dashboard"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: 12,
          padding: "10px 12px",
          borderRadius: 999,
          background: "#2563eb",
          color: "#fff",
          textDecoration: "none",
          justifyContent: expanded ? "flex-start" : "center",
        }}
      >
        <i className="fas fa-home" />
        {expanded && <span>Back to Dashboard</span>}
      </Link>

      {/* Menu */}
      <nav
        style={{
          maxHeight: "calc(100vh - 140px)",
          overflowY: "auto",
        }}
      >
        {MENU.map((item, idx) => {

          if (item.type === "group") {

            if (!expanded) return null;

            return (
              <div
                key={`grp-${idx}`}
                style={{
                  margin: "12px 14px 6px",
                  fontSize: 11,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </div>
            );
          }

          if (!canAny(permissions, item.perms)) return null;

          const active = location.pathname.startsWith(item.route);

          return (
            <NavLink
              key={item.route}
              to={item.route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: expanded ? "10px 14px" : "10px 0",
                justifyContent: expanded ? "flex-start" : "center",
                color: active ? "#fff" : "#cbd5e1",
                background: active ? "#374151" : "transparent",
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              <i className={item.icon} />
              {expanded && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}